"use client";

import type { PractitionerDto } from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/ui/popover";
import { Field, FieldError, FieldLabel } from "@repo/ui/form-fields";
import { ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { PractitionerDisplay } from "@/shared/common/practitioner-display";
import { useFieldContext } from "@/shared/forms/form-context";
import { getPractitionerName } from "@/shared/lib/practitioner-utils";

export interface PractitionerFieldProps {
  label?: string;
  placeholder?: string;
  practitioners: PractitionerDto[];
  disabled?: boolean;
  isLoading?: boolean;
}

export function PractitionerField({
  label,
  placeholder = "Select practitioner...",
  practitioners,
  disabled,
  isLoading,
}: PractitionerFieldProps) {
  const field = useFieldContext<string>();
  const [open, setOpen] = useState(false);

  const selectedPractitioner = useMemo(() => {
    if (!field.state.value) return null;
    return practitioners.find((p) => p.practitionerId === field.state.value) ?? null;
  }, [practitioners, field.state.value]);

  return (
    <Field errors={field.state.meta.errors} isTouched={field.state.meta.isTouched}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) field.handleBlur();
        }}
      >
        <PopoverTrigger
          render={<Button variant="outline" className={`w-full justify-between ${label ? "mt-1" : ""}`} />}
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <span className="text-muted-foreground">Loading...</span>
          ) : selectedPractitioner ? (
            <PractitionerDisplay practitionerId={selectedPractitioner.practitionerId} />
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </PopoverTrigger>
        <PopoverContent className="w-(--anchor-width) p-0" align="start">
          <Command>
            <CommandInput placeholder="Search practitioners..." />
            <CommandList>
              <CommandEmpty>No practitioners found.</CommandEmpty>
              <CommandGroup>
                {practitioners
                  .filter((p) => p.practitionerId)
                  .map((p) => (
                    <CommandItem
                      key={p.practitionerId}
                      value={getPractitionerName(p)}
                      onSelect={() => {
                        field.handleChange(p.practitionerId);
                        setOpen(false);
                      }}
                    >
                      <PractitionerDisplay practitionerId={p.practitionerId} />
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FieldError />
    </Field>
  );
}
